/**
 * "Últimas vezes": os atendimentos do paciente vinculado no SISAC, como a tela
 * de lá mostra, agrupados pela clínica do grupo. Só leitura.
 *
 * Os textos de situação e tipo vêm como a clínica os escreve ("Fechada",
 * "Exame"): o CRM não traduz o que não decide.
 */

import type { ReactNode } from 'react';
import type { UltimasVezesDoPaciente } from '@cliqcrm/contracts';
import { useUltimasVezesDoPaciente } from '@/shared/api/consultas';
import { Botao, Dialogo, Tabela, type ColunaTabela } from '@/shared/ui';
import { Carregando } from '@/shared/ui/estados/estados';
import { Faixa } from '@/shared/ui/estados/faixa';
import { recadoDoSisac } from '../model/usar-paciente-no-sisac';
import { gruposDasVezes } from '../lib/ultimas-vezes';

const COLUNAS: ColunaTabela[] = [
  { chave: 'data_hora', rotulo: 'Data e hora' },
  { chave: 'situacao', rotulo: 'Situação' },
  { chave: 'movimento', rotulo: 'Movimento', titulo: 'O número do atendimento no SISAC' },
  { chave: 'tipo', rotulo: 'Tipo' },
  { chave: 'medico', rotulo: 'Médico' },
  { chave: 'procedimentos', rotulo: 'Histórico', titulo: 'O que foi feito no atendimento' },
  { chave: 'convenio', rotulo: 'Convênio' },
  { chave: 'plano', rotulo: 'Plano' },
  { chave: 'local_origem', rotulo: 'Local de origem' },
];

export function UltimasVezes({
  clienteId,
  nome,
  aoFechar,
}: {
  clienteId: string;
  nome: string | null;
  aoFechar: () => void;
}): ReactNode {
  const leitura = useUltimasVezesDoPaciente(clienteId, true);
  return (
    <Dialogo
      titulo={nome ? `Últimas vezes de ${nome}` : 'Últimas vezes'}
      largura="larga"
      aoFechar={aoFechar}
      botoes={
        <Botao variante="secundario" onClick={aoFechar}>
          Fechar
        </Botao>
      }
    >
      <Conteudo leitura={leitura} />
    </Dialogo>
  );
}

function Conteudo({
  leitura,
}: {
  leitura: ReturnType<typeof useUltimasVezesDoPaciente>;
}): ReactNode {
  if (leitura.isPending) {
    return <Carregando forma="linha" linhas={4} rotulo="Lendo os atendimentos no SISAC…" />;
  }
  if (leitura.isError) {
    return (
      <Faixa tom="bloqueado">
        {recadoDoSisac(leitura.error, 'Não foi possível ler os atendimentos no SISAC.')}
      </Faixa>
    );
  }
  return <Atendimentos dados={leitura.data} />;
}

function Atendimentos({ dados }: { dados: UltimasVezesDoPaciente }): ReactNode {
  if (dados.vezes.length === 0) {
    return <p role="status">O SISAC não tem atendimentos deste paciente.</p>;
  }
  return (
    <Tabela
      colunas={COLUNAS}
      grupos={gruposDasVezes(dados.vezes)}
      linhas={[]}
      chaveDe={(_linha, indice) => String(indice)}
      densa
      rotulo="Atendimentos do paciente no SISAC"
      legenda={
        dados.cortada
          ? 'O SISAC mandou só os atendimentos mais recentes. Os antigos ficam na ficha de lá.'
          : undefined
      }
    />
  );
}
